<template>
    <div class="gap-md p-xs relative col-span-4 flex flex-col pr-6">
        <!-- Might want to refactor this absolute divider into the top level grid -->
        <div class="absolute -top-16 right-0 h-[calc(100%+7rem)] w-1 bg-primary" />
        <div class="px-md flex flex-col gap-2">
            <h2>Create a new organization</h2>
            <p class="text-body">To create a new organization, you need to go through several stages:</p>
        </div>
        <TransitionGroup>
            <div
                v-for="(tab, i) in tab_names.filter((t) => t !== 'Contract' || !orgIsContractor)"
                :key="tab + i"
                class="p-sm pl-md w-full cursor-pointer rounded-sm transition-all"
                :class="{ 'bg-soft': activeTab === tab }"
            >
                <div
                    :key="tab + i"
                    class="gap-sm flex items-start hover:cursor-pointer"
                    @click="
                        () => {
                            if (isTabDone(tab) || org_form_info?.next_step === tab) chooseTab(tab);
                        }
                    "
                >
                    <div
                        class="size-md flex aspect-square items-center justify-center rounded-sm transition-all"
                        :class="{
                            'bg-negative text-negative': activeTab === tab,
                            'bg-primary': activeTab !== tab,
                        }"
                    >
                        <i v-if="isTabDone(tab)" class="i-check-done" :class="{ invert: activeTab === tab }" />
                        <span v-else>{{ i + 1 }}</span>
                    </div>
                    <div class="gap-sm flex flex-col items-start">
                        <h3>{{ tab }}</h3>
                        <p class="text-normal text-start">{{ temp_desc[i] }}</p>
                        <OrgInfo
                            v-if="tab === 'info' && org_form_info"
                            :class="'!flex-row items-center justify-between'"
                            :company="org_form_info.organization.name"
                            :level="org_form_info.organization.service_level_id"
                        />
                    </div>
                    <div class="flex">
                        <i class="size-4" :class="{ 'i-arrow-right': activeTab === tab }" />
                    </div>
                </div>
            </div>
        </TransitionGroup>
    </div>
    <div class="py-xs col-span-8 flex flex-col overflow-auto">
        <component
            :is="getStepComponent(activeTab)"
            v-bind="getStepProps(activeTab)"
            @cancel="cancel"
            @submit="(val: any) => getSubmitHandler(activeTab)?.(val)"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { useAdminStore } from '@/stores/admin';

import InfoStep from '@/components/admin/organization/creation-process/info-step.vue';
import ContractStep from '@/components/admin/organization/creation-process/contract-step.vue';
import PoolStep from '@/components/admin/organization/creation-process/pool-step.vue';
import GroupStep from '@/components/admin/organization/creation-process/group-step.vue';
import SummaryStep from '@/components/admin/organization/creation-process/summary-step.vue';
import {
    type AdminOrganizationFormData,
    type AdminOrganizationDefaultGroup,
    type AdminOrgInfoBody,
    type Role,
    type UUIDLocation,
    type AdminSLAType,
    type AdminSLABody,
    type AdminServiceDeskReadOnly,
    type AdminContract,
    type OrganizationType,
} from '@/stores/store-interfaces';
import router from '@/router/admin-router';
import OrgInfo from '@/components/admin/organization/creation-process/org-info.vue';
import {
    getLockedDefaultsRoleById,
    isContractorOrganization,
} from '@/components/admin/organization/creation-process/organization-type-service-desk-defaults';

const props = defineProps({
    formId: String,
});

const adminStore = useAdminStore();

const tab_names = ref(['Organization Info']);

const loadingResponse = ref(false);

const all_roles = ref<Role[]>([]);
const org_groups = ref<{
    selected: AdminOrganizationDefaultGroup[];
    unselected: AdminOrganizationDefaultGroup[];
}>({ selected: [], unselected: [] });
const org_form_info = ref<AdminOrganizationFormData>();
const org_form_sla_types = ref<AdminSLAType[]>([]);
const org_form_contract_data = ref<AdminContract>();
const org_form_sla_lines = ref<AdminContract['sla']>([]);
const org_roles = ref<Role[]>([]);
const org_regions = ref<UUIDLocation[]>([]);
const org_service_desks = ref<AdminServiceDeskReadOnly[]>([]);
const organizationTypes = ref<OrganizationType[]>([]);
const orgIsContractor = computed(() => {
    return isContractorOrganization(org_form_info.value?.organization.organization_type);
});
const temp_desc = ref<string[]>([]);

const stepSlugToTab = (slug: unknown) => {
    if (typeof slug !== 'string' || slug === '') return undefined;
    return slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

onMounted(async () => {
    all_roles.value = await adminStore.getAllRoles();
    organizationTypes.value = await adminStore.getOrganizationTypes();

    if (!props.formId) {
        activeTab.value = tab_names.value[0];
    } else {
        const form = await loadFormInfo(Number(props.formId));
        if (orgIsContractor.value) {
            tab_names.value = ['Organization Info', 'Role Pool', 'Default Groups', 'Summary'];
            temp_desc.value = [
                'The Customer Design Implementation Manager starts by entering general information about the organization.',
                'The Customer Design Implementation Manager assigns specific roles to the organization.',
                'The Customer Design Implementation Manager also assigns base groups to the organization.',
                'The NOC Manager reviews and validates all the entered information, completing the creation of the new organization.',
            ];
        } else {
            tab_names.value = ['Organization Info', 'Contract', 'Role Pool', 'Default Groups', 'Summary'];
            temp_desc.value = [
                'The Customer Design Implementation Manager starts by entering general information about the organization.',
                'The Customer Design Implementation Manager assigns specific SLA lines to the contract.',
                'The Customer Design Implementation Manager assigns specific roles to the organization.',
                'The Customer Design Implementation Manager also assigns base groups to the organization.',
                'The NOC Manager reviews and validates all the entered information, completing the creation of the new organization.',
            ];
        }
        const orgId = form.organization_id;
        org_groups.value = await adminStore.getAllGroupsByOrgId(orgId);
        org_form_sla_types.value = await adminStore.getAllSLATypes();
        org_form_contract_data.value = await adminStore.getContractDataByOrgId(orgId);
        org_form_sla_lines.value = org_form_contract_data.value?.sla;

        org_roles.value = await adminStore.getRolesByOrgId(orgId);
        const organization = await adminStore.getOrganizationById(orgId);
        org_regions.value = await adminStore.getLocationsByOrgId(organization.organization_uuid);
        org_service_desks.value = await adminStore.getServiceDesksByOrgId(orgId).then((data) =>
            data.map((desk) => {
                return {
                    id: desk.service_desk_id,
                    name: desk.name,
                    request_types:
                        desk.request_types?.map((req) => {
                            return {
                                id: req.request_type_id,
                                name: req.name,
                            };
                        }) ?? [],
                } as unknown as AdminServiceDeskReadOnly;
            }),
        );

        const chosenTab = stepSlugToTab(router.currentRoute.value.query['step']);
        chooseTab(chosenTab ?? form.next_step);
    }
});

/**
 * (Re)load the creation form. Its next_step and status drive the tab checkmarks, the
 * status bar and the Summary step's action (Review for the CDIM, Create organization for
 * the NOC). Step submits navigate query-only and no longer remount this view (App.vue keys
 * RouterView by $route.path), so every successful step re-runs this. Otherwise the Summary
 * step submits a stale step name and its button spins forever (ITDEVELOP-3402).
 */
const loadFormInfo = async (formId: number) => {
    const form = await adminStore.getFormDataByFormId(formId);
    if (form.next_step === '' && form.status === 'APPROVED') {
        form.next_step = 'Summary';
    }
    if (form.next_step === 'Contract' && isContractorOrganization(form.organization.organization_type)) {
        form.next_step = 'Role Pool';
    }
    org_form_info.value = form;
    return form;
};

/** Refresh after a step landed. A failed refresh must not undo a successful submit. */
const refreshFormInfoAfterStep = async () => {
    if (!props.formId) return;
    try {
        await loadFormInfo(Number(props.formId));
    } catch (error) {
        console.error('Refreshing the creation form after a step failed:', error);
    }
};
// Helper functions for dynamic step component rendering
const getStepComponent = (tab: string) => {
    switch (tab) {
        case 'Organization Info':
            return InfoStep;
        case 'Contract':
            return ContractStep;
        case 'Role Pool':
            return PoolStep;
        case 'Default Groups':
            return GroupStep;
        case 'Summary':
            return SummaryStep;
        default:
            return null;
    }
};

function getTotalLocationCount(locationFootprint: UUIDLocation[]): number {
    let count = 0;

    for (const location of locationFootprint) {
        count += 1;
        count += getTotalLocationCount(location.children);
    }

    return count;
}

const getStepProps = (tab: string) => {
    const common = { disable: isTabDone(tab) || loadingResponse.value };
    switch (tab) {
        case 'Organization Info':
            return {
                ...common,
                locked: !!props.formId,
                formInfo: org_form_info.value,
                desks: org_service_desks.value,
                loading: loadingResponse.value,
            };
        case 'Contract':
            return {
                ...common,
                slatypes: org_form_sla_types.value,
                contractData: org_form_contract_data.value,
                loading: loadingResponse.value,
            };
        case 'Role Pool':
            return {
                ...common,
                allRoles: all_roles.value,
                selectedRoles: org_roles.value,
                serviceLevel: org_form_info.value?.organization.service_level_id,
                loading: loadingResponse.value,
            };
        case 'Default Groups':
            return {
                ...common,
                selected: org_groups.value.selected,
                unselected: org_groups.value.unselected,
                loading: loadingResponse.value,
            };
        case 'Summary':
            return {
                data: org_form_info.value,
                roles: org_roles.value,
                groups: org_groups.value,
                locations: org_regions.value,
                numberOfSelectedLocations: getTotalLocationCount(org_regions.value),
                serviceDesks: org_service_desks.value,
                nextStep: org_form_info.value?.next_step,
                slaLines: org_form_sla_lines.value,
                loading: loadingResponse.value,
            };
        default:
            return {};
    }
};

const getSubmitHandler = (tab: string) => {
    switch (tab) {
        case 'Organization Info':
            return (event: AdminOrgInfoBody) => submitInfo(event);
        case 'Contract':
            return (event: AdminSLABody) => submitContract(event);
        case 'Role Pool':
            return (event: Array<{ organization_role_id: number }>) => submitPool(event);
        case 'Default Groups':
            return (event: Array<{ default_group_id: number }>) => submitGroups(event);
        case 'Summary':
            return (event: string) => submitSummary(event);
        default:
            return () => {};
    }
};

const activeTab = ref<string>('');

// Submit handlers navigate between steps via router.replace with a new ?step query.
// App.vue keys RouterView by $route.path, so a query-only navigation does not remount
// this view — the tab has to be switched reactively from the query param.
watch(
    () => router.currentRoute.value.query['step'],
    (step) => {
        const tab = stepSlugToTab(step);
        if (tab && getStepComponent(tab)) chooseTab(tab);
    },
);

const chooseTab = (tab: string) => {
    if (tab === 'Review') {
        tab = 'Summary';
    }
    activeTab.value = tab;
};

const cancel = () => {
    router.push({ name: 'organizations' });
};

const submitInfo = async (body: AdminOrgInfoBody) => {
    loadingResponse.value = true;
    const isContractor =
        getLockedDefaultsRoleById(body.organization_creation_form.organization_type_id, organizationTypes.value) ===
        'contractor';
    if (!body) return;
    try {
        const response = await adminStore.startCreationProcess(body);
        adminStore.setToastMessage('Organization created successfully', 'check');

        if (isContractor) {
            await submitPool(
                org_roles.value.map((role) => ({
                    organization_role_id: role.organization_role_id,
                })),
                response.organization_creation_form_id,
                response.organization_id,
            );
            router.replace({
                name: 'org-edit',
                params: { form_id: response.organization_creation_form_id },
                query: { step: 'default-groups' },
            });
        } else {
            router.replace({
                name: 'org-edit',
                params: { form_id: response.organization_creation_form_id },
                query: { step: 'contract' },
            });
        }
        loadingResponse.value = false;
    } catch {
        adminStore.setToastMessage('Failed to create organization, please try again', 'warning');
        loadingResponse.value = false;
    }
};

const submitContract = async (body: AdminSLABody) => {
    if (!props.formId) return;
    loadingResponse.value = true;
    try {
        await adminStore.submitContractStep(Number(props.formId), body);
        adminStore.setToastMessage('Contract submitted successfully', 'check');
        await refreshFormInfoAfterStep();
        router.replace({
            name: 'org-edit',
            params: { form_id: props.formId },
            query: { step: 'role-pool' },
        });
    } catch {
        adminStore.setToastMessage('Failed to submit contract, please try again', 'warning');
    }
    setTimeout(() => (loadingResponse.value = false), 1000);
};

const submitPool = async (
    roles: Array<{ organization_role_id: number }>,
    formIdBecauseWeAreCreatingAContractOrg?: number,
    orgId?: number,
) => {
    if (!props.formId && !formIdBecauseWeAreCreatingAContractOrg) return;
    try {
        loadingResponse.value = true;
        const response = await adminStore.submitPoolStep(Number(org_form_info.value?.organization_id ?? orgId), roles);
        console.log('pool submit response', response);
        adminStore.setToastMessage('Role pool submitted successfully', 'check');
        // No-op while a contractor org is still being created (no form_id yet): that path
        // moves to a new route and remounts, so onMounted loads the form.
        await refreshFormInfoAfterStep();
        router.replace({
            name: 'org-edit',
            params: { form_id: props.formId ?? formIdBecauseWeAreCreatingAContractOrg },
            query: { step: 'default-groups' },
        });
    } catch {
        adminStore.setToastMessage('Failed to submit role pool, please try again', 'warning');
    }
    setTimeout(() => (loadingResponse.value = false), 1000);
};

const submitGroups = async (body: Array<{ default_group_id: number }>) => {
    if (!props.formId) return;

    try {
        loadingResponse.value = true;
        await adminStore.submitGroupStep(Number(props.formId), body);
        adminStore.setToastMessage('Groups submitted successfully', 'check');
        await refreshFormInfoAfterStep();
        router.replace({
            name: 'org-edit',
            params: { form_id: props.formId },
            query: { step: 'summary' },
        });
    } catch {
        adminStore.setToastMessage('Failed to submit groups, please try again', 'warning');
    }
    setTimeout(() => (loadingResponse.value = false), 1000);
};

const submitSummary = async (step: string) => {
    if (!props.formId) return;

    loadingResponse.value = true;
    if (step !== 'Summary' && step !== 'Review') {
        // The Summary step echoes the form's next_step; anything else means the loaded form
        // is out of date. Refresh it instead of leaving the button spinning with no request.
        await refreshFormInfoAfterStep();
        adminStore.setToastMessage('The form was out of date and has been refreshed, please try again', 'warning');
        loadingResponse.value = false;
    } else if (step === 'Summary') {
        try {
            await adminStore.submitSummaryStepToReview(Number(props.formId));

            adminStore.setToastMessage('Organization submitted for review', 'check');
            router.push({ name: 'organizations' });
        } catch {
            adminStore.setToastMessage('Failed to submit summary, please try again', 'warning');
        }
        setTimeout(() => (loadingResponse.value = false), 1000);
    } else if (step === 'Review') {
        console.log('submitting review');

        try {
            await adminStore.submitSummaryStep(Number(props.formId));
            adminStore.setToastMessage('Organization created', 'check');
            router.push({ name: 'organizations' });
        } catch {
            adminStore.setToastMessage('Failed to submit summary, please try again', 'warning');
        }
        setTimeout(() => (loadingResponse.value = false), 1000);
    }
};

const isTabDone = (tab: (typeof tab_names.value)[number]) => {
    if (!org_form_info.value) {
        return false;
    }

    const next = org_form_info.value.next_step;
    if (next === 'Review') {
        return true;
    }
    const options = ['Contract', 'Role Pool', 'Default Groups', 'Summary'];

    if (isContractorOrganization(org_form_info.value.organization.organization_type)) {
        options.splice(1, 1); // Remove 'Contract' for FE orgs
    }
    if (options.indexOf(tab) < options.indexOf(next)) {
        return true;
    }
    return false;
};
</script>
